// laserService.js
// BlindsPro CRM: Bluetooth laser measure support
// Bosch GLM (VERIFIED on GLM165-27CG) and Leica DISTO (BETA, untested on real device)

import { BleClient, hexStringToDataView, textToDataView } from '@capacitor-community/bluetooth-le';

// ---------- Brand adapters ----------

const BOSCH = {
  brand: 'Bosch',
  beta: false,
  service: '02a6c0d0-0451-4000-b000-fb3210111989',
  characteristic: '02a6c0d1-0451-4000-b000-fb3210111989',
  initHex: 'C0550201001A',
  // Measurement: C0 55 10 06 00 [counter] 00 [float32 LE metres at bytes 7-10]
  parse(dv) {
    if (dv.byteLength < 11) return null;
    const b0 = dv.getUint8(0), b1 = dv.getUint8(1), b2 = dv.getUint8(2), b3 = dv.getUint8(3);
    if (b0 !== 0xC0 || b1 !== 0x55 || b2 !== 0x10 || b3 !== 0x06) return null; // keep-alive / events ignored
    const counter = dv.getUint8(5);
    const meters = dv.getFloat32(7, true);
    return { meters, counter };
  },
};

const LEICA = {
  brand: 'Leica',
  beta: true,
  service: '3ab10100-f831-4395-b29d-570977d5bf94',
  characteristic: '3ab10101-f831-4395-b29d-570977d5bf94', // distance, float32 LE metres
  commandChar: '3ab10109-f831-4395-b29d-570977d5bf94',
  initCommands: ['getSoftwareVersion app', 'raiseEvent 300'],
  parse(dv) {
    if (dv.byteLength < 4) return null;
    const meters = dv.getFloat32(0, true);
    return { meters, counter: null };
  },
};

const ADAPTERS = [BOSCH, LEICA];

// ---------- State ----------

let connectedDeviceId = null;
let activeAdapter = null;
let lastCounter = null;
let initialized = false;

// ---------- Helpers ----------

export function metersToInches(meters) {
  const totalInches = meters * 39.3700787;
  const sixteenths = Math.round(totalInches * 16);
  const wholeInches = Math.floor(sixteenths / 16);
  const fraction16 = sixteenths % 16; // 0..15 (e.g. 8 = 1/2")
  return {
    meters,
    totalInches: sixteenths / 16,
    feet: Math.floor(wholeInches / 12),
    inches: wholeInches % 12,
    wholeInches,
    fraction16,
    label: formatInches(wholeInches, fraction16),
  };
}

function formatInches(whole, f16) {
  if (f16 === 0) return `${whole}"`;
  let n = f16, d = 16;
  while (n % 2 === 0) { n /= 2; d /= 2; }
  return `${whole} ${n}/${d}"`;
}

function isPlausible(m) {
  return Number.isFinite(m) && m > 0.03 && m < 300;
}

// ---------- Auto-find ----------

const NAME_PATTERNS = [/^GLM/i, /DISTO/i];

function looksLikeLaser(result) {
  const name = (result.localName || (result.device && result.device.name) || '').trim();
  if (NAME_PATTERNS.some(re => re.test(name))) return true;
  const uuids = (result.uuids || []).map(u => String(u).toLowerCase());
  return ADAPTERS.some(a => uuids.includes(a.service));
}

/**
 * Scan up to 5 s for Bosch GLM / Leica DISTO lasers.
 * Stops 1.5 s after the first match and returns the closest (strongest signal).
 * Returns a BleDevice or null.
 */
async function autoFindLaser() {
  const found = new Map(); // deviceId -> { device, rssi }
  let earlyTimer = null;

  return new Promise(async (resolve) => {
    let done = false;
    const finish = async () => {
      if (done) return;
      done = true;
      clearTimeout(maxTimer);
      clearTimeout(earlyTimer);
      try { await BleClient.stopLEScan(); } catch (e) { /* ignore */ }
      let best = null;
      found.forEach(v => { if (!best || v.rssi > best.rssi) best = v; });
      resolve(best ? best.device : null);
    };
    const maxTimer = setTimeout(finish, 5000);

    try {
      await BleClient.requestLEScan({ allowDuplicates: false }, (result) => {
        if (!result || !result.device || !looksLikeLaser(result)) return;
        const rssi = typeof result.rssi === 'number' ? result.rssi : -999;
        const prev = found.get(result.device.deviceId);
        if (!prev || rssi > prev.rssi) found.set(result.device.deviceId, { device: result.device, rssi });
        if (!earlyTimer) earlyTimer = setTimeout(finish, 1500);
      });
    } catch (e) {
      console.warn('Laser auto-find scan failed, falling back to picker:', e);
      finish();
    }
  });
}

// ---------- Public API ----------

export function getConnectedInfo() {
  if (!connectedDeviceId || !activeAdapter) return null;
  return { deviceId: connectedDeviceId, brand: activeAdapter.brand, beta: activeAdapter.beta };
}

/**
 * Connect to a laser. Shows the system device picker.
 * onMeasurement({ meters, totalInches, feet, inches, fraction16, label, brand })
 * onDisconnect() called if the laser drops.
 * Returns { brand, beta }.
 */
export async function connectLaser(onMeasurement, onDisconnect) {
  if (!initialized) {
    await BleClient.initialize({ androidNeverForLocation: true });
    initialized = true;
  }

  if (connectedDeviceId) await disconnectLaser();

  // 1) Try auto-find (no list). 2) Fall back to the full device picker.
  let device = await autoFindLaser();
  if (!device) {
    device = await BleClient.requestDevice({
      optionalServices: ADAPTERS.map(a => a.service),
    });
  }

  await BleClient.connect(device.deviceId, () => {
    connectedDeviceId = null;
    activeAdapter = null;
    if (onDisconnect) onDisconnect();
  });

  // Detect brand by service UUID
  const services = await BleClient.getServices(device.deviceId);
  const uuids = services.map(s => s.uuid.toLowerCase());
  const adapter = ADAPTERS.find(a => uuids.includes(a.service));
  if (!adapter) {
    await BleClient.disconnect(device.deviceId);
    throw new Error('This device is not a supported laser (Bosch GLM or Leica DISTO).');
  }

  connectedDeviceId = device.deviceId;
  activeAdapter = adapter;
  lastCounter = null;

  await BleClient.startNotifications(device.deviceId, adapter.service, adapter.characteristic, (dv) => {
    const r = adapter.parse(dv);
    if (!r || !isPlausible(r.meters)) return;
    if (r.counter !== null) {
      if (r.counter === lastCounter) return; // duplicate packet
      lastCounter = r.counter;
    }
    onMeasurement({ ...metersToInches(r.meters), brand: adapter.brand });
  });

  // Brand specific init
  if (adapter === BOSCH) {
    await BleClient.write(device.deviceId, BOSCH.service, BOSCH.characteristic, hexStringToDataView(BOSCH.initHex));
  } else if (adapter === LEICA) {
    for (const cmd of LEICA.initCommands) {
      try {
        await BleClient.write(device.deviceId, LEICA.service, LEICA.commandChar, textToDataView(cmd + '\r\n'));
      } catch (e) {
        console.warn('Leica init command failed (may be OK):', cmd, e);
      }
    }
  }

  return { brand: adapter.brand, beta: adapter.beta };
}

export async function disconnectLaser() {
  if (!connectedDeviceId) return;
  const id = connectedDeviceId;
  const adapter = activeAdapter;
  connectedDeviceId = null;
  activeAdapter = null;
  try { await BleClient.stopNotifications(id, adapter.service, adapter.characteristic); } catch (e) { /* ignore */ }
  try { await BleClient.disconnect(id); } catch (e) { /* ignore */ }
}
